/**
 * Login Page Component
 * Handles user authentication with email/password or social login
 * Redirects to gallery after successful login
 *
 * @module pages/LoginPage
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '@/utils/logger'
import { Layout } from '@/components/layout'
import { LoginForm } from '@/components/auth'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/utils/constants'

/**
 * Login page component
 * Displays login form and handles navigation after successful authentication
 * Redirects to gallery if already authenticated
 */
export default function LoginPage() {
  const navigate = useNavigate()
  const auth = useAuth()

  logger.debug('[WALRUS] LoginPage: rendering login page', {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
  })

  // Redirect to gallery if already authenticated
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      logger.debug('[WALRUS] LoginPage: user already authenticated, redirecting to gallery')
      navigate(ROUTES.GALLERY, { replace: true })
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate])

  /**
   * Handle successful login
   */
  const handleLoginSuccess = () => {
    logger.info('[WALRUS] LoginPage: login successful, redirecting to gallery')
    navigate(ROUTES.GALLERY, { replace: true })
  }

  return (
    <Layout contentClassName="flex items-center justify-center min-h-[calc(100vh-128px)] px-4 py-12 sm:px-6 lg:px-8 bg-walrus-white">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <img
            src="/walrus-logo.png"
            alt="Rodrigo the Walrus"
            className="w-24 h-24 mx-auto mb-6 object-contain"
          />
          <h1 className="text-4xl font-display font-bold text-walrus-black mb-2 tracking-wider">
            SIGN IN
          </h1>
          <p className="text-walrus-silver">
            Welcome back to the family
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
          <LoginForm onSuccess={handleLoginSuccess} />

          {/* Help Text */}
          <div className="mt-8 pt-8 border-t border-walrus-gray text-center text-walrus-silver text-sm">
            <p className="mb-4">Not a member yet?</p>
            <p className="text-walrus-silver">
              Membership is invite-only. Contact an existing member.
            </p>
          </div>
        </div>

        {/* Security Note */}
        <div className="text-center text-xs text-walrus-silver px-4">
          <p>Secure connection. Your data stays with the family.</p>
        </div>
      </div>

      {/* TODO: Add social login options (Google, GitHub) */}
      {/* TODO: Add password reset flow */}
      {/* TODO: Add account creation flow */}
    </Layout>
  )
}
